/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { InputNumberModule } from "primeng/inputnumber";
import { ScheduledOrdersHelper } from "../../helpers/scheduled-orders.helper";

/**
 * Select Frequency Component
 * Allows users to select frequency type and value for scheduled orders
 * Used inside the change-frequency-dialog
 */
@Component({
  selector: "app-frequency-selector",
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, InputNumberModule],
  templateUrl: "./select-frequency.component.html",
  styleUrl: "./select-frequency.component.scss",
})
export class SelectFrequencyComponent implements OnInit, OnChanges {
  @Input() frequencyType?: string;
  @Input() frequency?: number;
  @Output() selectFrequencyType = new EventEmitter<string>();
  @Output() selectFrequency = new EventEmitter<number>();

  showCustom = false;
  customFrequency?: number;

  frequencyTypes = [
    { label: "Number of Days", value: "Days" },
    { label: "Day of the week", value: "DayOfTheWeek" },
    { label: "Number of weeks", value: "Weeks" },
    { label: "Number of months", value: "Months" },
    { label: "Day of the month", value: "DayOfTheMonth" },
  ];

  daysFrequencyRange: number[] = [];
  dayOfTheWeekFrequencyRange: { value: number; name: string }[] = [];
  weeksFrequencyRange: number[] = [];
  monthsFrequencyRange: number[] = [];
  dayOfTheMonthFrequencyRange: number[][] = [];

  constructor(public scheduledOrdersHelper: ScheduledOrdersHelper) {}

  ngOnInit(): void {
    this.daysFrequencyRange = this.getDaysFrequencyRange();
    this.dayOfTheWeekFrequencyRange = this.getDayOfTheWeekFrequencyRange();
    this.weeksFrequencyRange = this.getWeeksFrequencyRange();
    this.monthsFrequencyRange = this.getMonthsFrequencyRange();
    this.dayOfTheMonthFrequencyRange = this.getDayOfTheMonthFrequencyRange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If we're in custom mode and frequency input changes from parent,
    // ignore it and keep showing "Custom" in dropdown
    if (changes["frequency"] && this.showCustom) {
      this.frequency = -1;
      if (changes["frequency"].currentValue) {
        this.customFrequency = changes["frequency"].currentValue;
      }
    }
  }

  /**
   * Handle frequency type selection change
   */
  onFrequencyTypeChange(): void {
    this.frequency = undefined;
    if (this.frequencyType === "Days") {
      this.frequency = 2;
      this.emitFrequency(this.frequency);
    }
    this.showCustom = false;
    this.emitFrequencyType(this.frequencyType || "");
  }

  /**
   * Handle frequency selection change
   */
  onFrequencyChange(value: number): void {
    this.frequency = value;
    if (value !== -1) {
      this.emitFrequency(this.frequency);
      this.showCustom = false;
    } else {
      // Keep frequency as -1 to maintain "Custom" selection in dropdown
      this.showCustom = true;
    }
  }

  /**
   * Handle custom frequency input
   */
  onCustomFrequencyInput(): void {
    if (this.customFrequency) {
      this.emitFrequency(this.customFrequency);
    }
  }

  /**
   * Handle radio button selection for day of week/month
   */
  onFrequencyRadioChange(frequency: number): void {
    this.frequency = frequency;
    this.emitFrequency(this.frequency);
  }

  /**
   * Get options for dropdown with custom option
   */
  getDropdownOptions(range: number[]): { label: string; value: number }[] {
    const options = range.map((val) => ({
      label: this.scheduledOrdersHelper.getFrequencyDisplayName(
        this.frequencyType || "",
        val,
      ),
      value: val,
    }));
    options.push({ label: "Custom", value: -1 });
    return options;
  }

  /**
   * Emit frequency type change
   */
  private emitFrequencyType(frequencyType: string): void {
    this.selectFrequencyType.emit(frequencyType);
  }

  /**
   * Emit frequency value change
   */
  private emitFrequency(frequency: number): void {
    this.selectFrequency.emit(frequency);
  }

  /**
   * Get days frequency range (2-7)
   */
  private getDaysFrequencyRange(): number[] {
    const days: number[] = [];
    for (let d = 2; d < 8; d++) {
      days.push(d);
    }
    return days;
  }

  /**
   * Get day of the week range (Sunday-Saturday)
   */
  private getDayOfTheWeekFrequencyRange(): {
    value: number;
    name: string;
  }[] {
    return [
      { value: 1, name: "Sun" },
      { value: 2, name: "Mon" },
      { value: 3, name: "Tue" },
      { value: 4, name: "Wed" },
      { value: 5, name: "Thu" },
      { value: 6, name: "Fri" },
      { value: 7, name: "Sat" },
    ];
  }

  /**
   * Get weeks frequency range (1-4)
   */
  private getWeeksFrequencyRange(): number[] {
    const weeks: number[] = [];
    for (let w = 1; w < 5; w++) {
      weeks.push(w);
    }
    return weeks;
  }

  /**
   * Get months frequency range (1-12)
   */
  private getMonthsFrequencyRange(): number[] {
    const months: number[] = [];
    for (let m = 1; m < 13; m++) {
      months.push(m);
    }
    return months;
  }

  /**
   * Get day of the month range (1-31 in calendar format)
   */
  private getDayOfTheMonthFrequencyRange(): number[][] {
    const daysOfTheMonth: number[][] = [];
    for (let w = 0; w < 5; w++) {
      daysOfTheMonth[w] = [];
      for (let d = 1, dd = 7 * w + d; d < 8 && dd < 32; d++, dd++) {
        daysOfTheMonth[w].push(dd);
      }
    }
    return daysOfTheMonth;
  }
}
